import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Badge, Button, Table, Textarea } from '@dreamtree-org/twreact-ui';
import { Download } from 'lucide-react';
import { SectionCard } from '@/components';
import { useMutation } from '@/lib/useQuery';
import { errorText } from '@/lib/format';
import { parseCsv } from '../lib/csv';
import { optionKey, rowProblems, variantColumns, variantRows, variantTemplate } from '../lib/variantCsv';

/**
 * The variant CSV (MERCH-88): one row per size / colour, grouped into products by handle. The server checks
 * every row and imports all or nothing; its problems are shown by row. A product the store already has gets the
 * rows as new variants of its listing (D109). `type`: the registry type picked.
 */
export default function VariantImport({ type }) {
  const nav = useNavigate();
  const submit = useMutation('crud');
  const [text, setText] = useState('');
  const [parsed, setParsed] = useState(null);
  const [problems, setProblems] = useState(null);
  const [done, setDone] = useState(null);
  const cols = variantColumns(type);
  const template = variantTemplate(type);

  const read = (csv) => { setDone(null); setProblems(null); setParsed(variantRows(parseCsv(csv), type)); };
  const run = async () => {
    setProblems(null);
    try {
      const res = await submit.run({ model: 'InventoryUnit', action: 'importVariants', data: { rows: parsed.rows } });
      setDone(res.data || res);
    } catch (ex) {
      const details = ex && ex.response && ex.response.data && ex.response.data.details;
      setProblems({ message: errorText(ex), byRow: rowProblems(details) });
    }
  };
  const download = () => {
    const url = URL.createObjectURL(new Blob([template], { type: 'text/csv' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `variants-${type.key}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };
  const products = parsed ? new Set(parsed.rows.map((r) => String(r.handle || '').toLowerCase()).filter(Boolean)).size : 0;

  return (
    <SectionCard title="One row per variant" actions={<Button size="sm" variant="outline" leftIcon={<Download size={14} />} onClick={download}>Download template</Button>}>
      <p className="text-sm"><span className="font-medium">Required:</span> {cols.required.join(', ')}</p>
      <p className="text-sm opacity-80"><span className="font-medium">Optional:</span> {cols.optional.join(', ')}</p>
      <p className="text-xs opacity-70">Rows with the same handle are one product; its first row names the brand, model, condition and specs. Amounts are in rupees. If any row has a problem nothing is imported.</p>
      <Textarea label="Paste CSV" rows={6} value={text} onChange={(e) => setText(e.target.value)} placeholder={cols.required.join(',')} />
      <div className="flex flex-wrap gap-2">
        <Button onClick={() => read(text)} disabled={!text.trim()}>Check</Button>
        <Button variant="ghost" onClick={() => { setText(template); read(template); }}>Try the sample</Button>
      </div>
      {parsed ? (
        <div className="space-y-3" data-testid="variant-import-review">
          {parsed.headerErrors.map((e) => <Alert key={e} variant="error">{e}</Alert>)}
          <p className="text-sm">{parsed.rows.length} variant row{parsed.rows.length === 1 ? '' : 's'} in {products} product{products === 1 ? '' : 's'}.</p>
          {problems ? (
            <Alert variant="error" title="Nothing was imported">
              <p className="text-sm">{problems.message}</p>
              <ul className="mt-2 list-disc pl-4 text-sm">
                {Object.entries(problems.byRow).map(([row, msgs]) => <li key={row}>{Number(row) ? `Row ${row}` : 'File'}: {msgs.join('; ')}</li>)}
              </ul>
            </Alert>
          ) : null}
          {done ? (
            <Alert variant="success" title="Import complete">
              Batch {done.batch_id}: {done.variants} variant{done.variants === 1 ? '' : 's'} imported: {done.products} new draft product{done.products === 1 ? '' : 's'}{done.extended ? `, ${done.extended} existing product${done.extended === 1 ? '' : 's'} given new variants` : ''}.
              <div className="mt-2"><Button size="sm" variant="outline" onClick={() => nav('/products?tab=listed')}>View in Products</Button></div>
            </Alert>
          ) : (
            <Button onClick={run} loading={submit.loading} disabled={!parsed.rows.length || parsed.headerErrors.length > 0}>Import {parsed.rows.length} variant{parsed.rows.length === 1 ? '' : 's'}</Button>
          )}
          {parsed.rows.length ? (
            <Table
              data={parsed.rows.map((r, i) => ({ id: i + 1, ...r }))}
              columns={[
                { key: 'id', label: 'Row' },
                { key: 'handle', label: 'Handle' },
                { key: 'variant', label: 'Variant', render: (r) => (type.options || []).map((o) => r[optionKey(o.name)]).filter(Boolean).join(' / ') || '—' },
                { key: 'sku', label: 'SKU' },
                { key: 'problem', label: 'Result', wrap: true, render: (r) => (problems && problems.byRow[r.id] ? <span className="text-sm text-error-600">{problems.byRow[r.id].join('; ')}</span> : <Badge variant={done ? 'success' : 'secondary'}>{done ? 'Imported' : 'Checked by the server on import'}</Badge>) },
              ]}
              showSerial={false}
              withAction={false}
              showColumnPicker={false}
              showReloadButton={false}
              pagination
              pageSize={25}
              wrapCells
            />
          ) : null}
        </div>
      ) : null}
    </SectionCard>
  );
}
